import { useSession } from '@/app/session';
import type { CabinetHeaderProps } from '@/app/shells/cabinet/types/CabinetHeaderProps';
import { primaryRole } from '@/features/common/labels';
import { Icon } from '@/features/common/Icon';
import { ProfileMenu } from '@/features/common/layout/ProfileMenu';
import '@/features/common/layout/site-header.css';
import { appShell } from '@/i18n/dictionaries/appShell';
import { roleLabel } from '@/i18n/roleLabels';
import { useTranslation } from '@/i18n/useTranslation';

/** The cabinet's header row: the AKIS brand, the zone's name, the profile menu, the drawer toggle. */
export function CabinetHeader({ drawerOpen, toggleRef, onToggle }: CabinetHeaderProps) {
  const { user } = useSession();
  const { t, language } = useTranslation(appShell);
  const displayRole = user ? primaryRole(user.roles) : undefined;

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <a href="/" className="brand" aria-label={t('brandAria')}>
          <span className="brand__mark" aria-hidden="true">
            <span className="brand__mark-text">AK</span>
          </span>
          <span className="brand__word" aria-hidden="true">
            AKIS
          </span>
        </a>
        <span className="site-header__zone">{t('zoneLabel')}</span>
        <div className="header-actions">
          {/* Shared with the public header (features/common/layout/ProfileMenu). The behaviour that
              used to live here — Escape closing and returning focus to the avatar, an outside
              click closing, the logout form POST that SAML single logout needs — was written
              three times: here, in FlowShell, and in site.js for the Thymeleaf header. */}
          {user && (
            <ProfileMenu
              fullName={user.fullName}
              roleLabel={displayRole ? roleLabel(displayRole, language) : null}
              profileHref="/app/profil"
            />
          )}
          {/* Icon-only, right-aligned per MUD's mobile header pattern (Components/elements/header.html
              .header__mobile .mainNav__toggle) — was previously a labelled primary button left of the
              logo, which doesn't match the reference's icon-button-after-actions placement. */}
          <button
            ref={toggleRef}
            type="button"
            className="drawer-toggle"
            aria-expanded={drawerOpen}
            aria-controls="cabinet-sidebar"
            aria-label={t('menuAria')}
            onClick={onToggle}
          >
            <Icon name="menu" className="drawer-toggle__icon" />
          </button>
        </div>
      </div>
    </header>
  );
}
